/**
 * Motion voting-lifecycle helpers shared across the API services and the clerk
 * console. The voting lifecycle is persisted as two nullable timestamps on
 * `MinutesMotion` (`votingOpenedAt` / `votingClosedAt`) and the tri-state is
 * derived from them — there is no stored enum. `closed` is sticky and independent
 * of `resultLegislativeStatusId`, so clearing a closed motion's votes (which can
 * reset the result to `null`) never reopens it.
 */
/** Derived voting lifecycle state for a motion. */
export const MotionVotingStatus = {
    closed: 'closed',
    not_opened: 'not_opened',
    open: 'open',
};
/**
 * Derives a motion's voting status from its two lifecycle timestamps.
 *
 * - `closed` — `votingClosedAt` is set (terminal; never cleared).
 * - `open` — voting was opened and not yet closed.
 * - `not_opened` — neither timestamp set.
 */
export const getVotingStatus = (votingOpenedAt, votingClosedAt) => {
    if (votingClosedAt != null) {
        return MotionVotingStatus.closed;
    }
    if (votingOpenedAt != null) {
        return MotionVotingStatus.open;
    }
    return MotionVotingStatus.not_opened;
};
/** Convenience boolean used by the member app to gate the voting takeover + casts. */
export const isVotingOpen = (votingOpenedAt, votingClosedAt) => getVotingStatus(votingOpenedAt, votingClosedAt) === MotionVotingStatus.open;
/**
 * Resolves the active motion = the top of the LIFO stack of motions that are **on the floor**.
 *
 * "On the floor" means recorded and unclosed: `recordedAt != null && votingClosedAt == null`.
 * Staged drafts (`recordedAt == null` — pre-meeting or editor-created motions across any item, plus
 * auto-seeded consent-outcome placeholders) are **not** on the floor and never join the stack, so
 * staging motions can't hijack the active pointer. The top is the newest by `(recordedAt, id)` —
 * `recordedAt` is the floor-entry time, so a motion staged earlier but recorded now correctly
 * becomes the active motion. Tie-break on `id` for same-timestamp recordings.
 *
 * This is the **write rule** used server-side to maintain `LiveMeetingState.activeMinutesMotionId`
 * (push on a recorded create, pop/recompute on record / close / delete) — never item-record
 * `orderById`, which is display order and differs for privileged shells / re-floored duplicates.
 * Returns `null` when no recorded, unclosed motions remain.
 */
export const pickActiveMotionId = (motions) => {
    let topId = null;
    let topRecordedAt = -Infinity;
    for (const motion of motions) {
        if (motion.votingClosedAt != null || motion.recordedAt == null) {
            continue;
        }
        const recordedAt = new Date(motion.recordedAt).getTime();
        if (topId == null ||
            recordedAt > topRecordedAt ||
            (recordedAt === topRecordedAt && motion.id > topId)) {
            topId = motion.id;
            topRecordedAt = recordedAt;
        }
    }
    return topId;
};
